import { computed, defineComponent, ref, toRefs, watch } from "@vue/composition-api";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import * as ApplicationActions from "@Broker/@Modules/clients/applications/application/store/application-actions";
import Badge from "@Shared/components/general/badge.vue";
import { getApplicationSyncEventTypeText } from "@Broker/js/custom/utils";
import { useI18n } from "vue-i18n-composable";
import SyncError from "./sync-error.vue";
const {
  useActions: useApplicationStoreActions
} = createNamespacedHelpers("ApplicationStore");
export default defineComponent({
  name: "ApplicationSyncHistory",
  components: {
    Badge,
    SyncError
  },
  props: {
    applicationId: {
      type: Number,
      required: true
    },
    clientUsername: {
      type: String,
      required: true
    },
    updatingSyncStatus: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const {
      t,
      tc
    } = useI18n();
    const syncHistoryTable = ref();
    const {
      applicationId,
      clientUsername,
      updatingSyncStatus
    } = toRefs(props);
    const loading = ref(false);
    const lastTableFetchLength = ref(-1);
    const eventTypes = [{
      id: "all",
      text: tc("global.words.all", 1)
    }, {
      id: "dealdelete",
      text: getApplicationSyncEventTypeText("dealdelete")
    }, {
      id: "dealnew",
      text: getApplicationSyncEventTypeText("dealnew")
    }, {
      id: "dealupdate",
      text: getApplicationSyncEventTypeText("dealupdate")
    }, {
      id: "submission",
      text: getApplicationSyncEventTypeText("submission")
    }, {
      id: "response",
      text: getApplicationSyncEventTypeText("response")
    }, {
      id: "accept",
      text: getApplicationSyncEventTypeText("accept")
    }, {
      id: "unaccept",
      text: getApplicationSyncEventTypeText("unaccept")
    }];
    const selectedEventType = ref("all");
    const {
      LOAD_APPLICATION_SYNC_HISTORY
    } = useApplicationStoreActions([ApplicationActions.LOAD_APPLICATION_SYNC_HISTORY]);
    const table = computed(() => {
      return {
        columns: ["createdAt", "result", "eventType", "title", "errorMessage"],
        options: {
          filterable: false,
          sortable: [],
          headings: {
            createdAt: t("global.doorr.dateCreated"),
            result: t("global.words.result"),
            eventType: t("application-form.type"),
            title: t("global.words.message"),
            errorMessage: t("filogix.expertSync.errorDetails")
          },
          columnsClasses: {
            createdAt: "w-30",
            result: "w-10",
            eventType: "w-10",
            title: "w-20",
            errorMessage: "w-30"
          },
          skin: "table mb-0",
          perPage: 20,
          tabbable: false,
          requestFunction: tableRequestFunction.bind(this),
          stickyHeader: true,
          pagination: {
            virtual: true
          },
          customFilters: ["eventType"]
        }
      };
    });
    const handleTableLoading = () => {
      loading.value = true;
    };
    const handleTableLoaded = () => {
      loading.value = false;
    };
    const tableRequestFunction = async tableOptions => {
      const tableData = {
        data: [],
        count: 9999999
      };
      const {
        page,
        limit
      } = tableOptions;
      const offset = (page - 1) * limit;
      if (lastTableFetchLength.value >= 0 && lastTableFetchLength.value < limit) return tableData;
      const data = await LOAD_APPLICATION_SYNC_HISTORY({
        applicationId: applicationId.value,
        clientUsername: clientUsername.value,
        offset,
        limit,
        eventType: selectedEventType.value === "all" ? null : selectedEventType.value
      });
      lastTableFetchLength.value = data.length;
      tableData.data = data;
      return tableData;
    };
    const handleFilterByEventType = eventType => {
      selectedEventType.value = eventType;
      syncHistoryTable.value.customQueries["eventType"] = eventType;
      refreshTable();
    };
    const refreshTable = () => {
      syncHistoryTable.value.setPage(1);
      lastTableFetchLength.value = -1;
      syncHistoryTable.value.refresh();
    };
    watch(updatingSyncStatus, (value, oldValue) => {
      if (!value && oldValue) refreshTable();
    });
    return {
      t,
      table,
      eventTypes,
      selectedEventType,
      syncHistoryTable,
      handleTableLoading,
      handleTableLoaded,
      getApplicationSyncEventTypeText,
      handleFilterByEventType
    };
  }
});